// Notice when the app on the server is newer than the app in this tab.
//
// A deploy replaces the code, but it cannot reach into a page that is already
// open. index.html is served no-store and the service worker asks the network
// for it first, so a RELOAD always gets the newest build — but a desktop
// browser left on the app since yesterday keeps running yesterday's
// JavaScript, and the buttons a deploy added simply are not there. From the
// far side of the screen that looks exactly like the app ignoring a click,
// which is how it was found (Julian, on the website, 2026-08-19).
//
// So: read which build the server is serving, remember it, and look again when
// the tab comes back to the front or enough time passes. If it has changed,
// tell the app, which offers a reload. Nothing reloads on its own — a page
// that refreshes itself under somebody mid-estimate is worse than a stale one.

const ENDPOINT = '/api/version';
const EVERY = 5 * 60 * 1000;

const read = async () => {
  try {
    const res = await fetch(ENDPOINT, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.build || null;
  } catch {
    return null;   // offline, or the server is mid-restart. Try again later.
  }
};

export function watchForNewBuild(onNewBuild) {
  let current = null;
  let stopped = false;

  const check = async () => {
    if (stopped) return;
    const build = await read();
    if (!build) return;
    // The first answer is only the baseline — it says what this tab is
    // running, not that anything is new.
    if (!current) { current = build; return; }
    if (build !== current) { stopped = true; onNewBuild(); }
  };

  check();
  const timer = setInterval(check, EVERY);
  // Coming back to the tab is the moment worth checking: it is when somebody
  // is about to use it, and when a deploy has most likely happened since.
  const onFocus = () => { if (document.visibilityState === 'visible') check(); };
  window.addEventListener('visibilitychange', onFocus);
  window.addEventListener('focus', onFocus);

  return () => {
    stopped = true;
    clearInterval(timer);
    window.removeEventListener('visibilitychange', onFocus);
    window.removeEventListener('focus', onFocus);
  };
}
